import {
  Box,
  Button,
  Collapse,
  Divider,
  Paper,
  Stack,
  Switch,
  SxProps,
  Typography,
} from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';

type FormSectionButtonProps = {
  label: string;
  icon: React.ReactNode;
  action: () => any;
};

export type FormSectionProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  button?: FormSectionButtonProps;
  action?: React.ReactNode;
  collapsed?: boolean;
  onCollapsed?: (checked: boolean) => void;
  disableSwitch?: boolean;
  'data-testid'?: string;
  sx?: SxProps;
  contentSx?: SxProps;
};

const FormSection = ({
  title,
  subtitle,
  button,
  action,
  collapsed,
  onCollapsed,
  children,
  disableSwitch = false,
  'data-testid': dataTestId = 'FormSection',
  sx,
  contentSx,
}: FormSectionProps) => {
  const [_collapsed, setCollapsed] = useState(collapsed ?? false);

  useEffect(() => {
    if (typeof collapsed === 'boolean') {
      setCollapsed(collapsed);
    }
  }, [collapsed]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCollapsed(!event.target.checked);
    onCollapsed?.(event.target.checked);
  };

  return (
    <Paper
      elevation={3}
      data-testid={dataTestId}
      sx={theme => ({
        maxWidth: theme.forms?.maxWidth,
        ...(sx as object),
      })}
    >
      <Stack
        p={2}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        direction="row"
      >
        <Box>
          {typeof title === 'string' ? (
            <Typography
              variant="h4"
              component="h2"
              data-testid="form.section.title"
            >
              {title}
            </Typography>
          ) : (
            title
          )}
          {subtitle && (
            <Typography
              component={Box}
              fontStyle="italic"
              variant="body2"
              color="text.secondary"
              data-testid="form.section.subtitle"
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box>
          {button && (
            <Button
              onClick={button.action}
              startIcon={button.icon}
              data-testid="form.section.button"
              sx={{ whiteSpace: 'nowrap' }}
              variant="text"
            >
              {button.label}
            </Button>
          )}
          {action && action}
          {typeof collapsed === 'boolean' && (
            <Switch
              checked={!_collapsed}
              onChange={handleChange}
              inputProps={{ 'aria-label': 'Enable Section' }}
              disabled={disableSwitch}
            />
          )}
        </Box>
      </Stack>
      {children && (
        <Collapse in={!_collapsed}>
          <Divider />
          <Content sx={contentSx}>{children}</Content>
        </Collapse>
      )}
    </Paper>
  );
};

const Content = ({ sx, children }: { sx?: SxProps; children: ReactNode }) => {
  return (
    <Box
      padding={2}
      data-testid="form.section.content"
      alignItems="center"
      flexWrap="wrap"
      sx={_theme => ({
        ...(sx as object),
      })}
    >
      {children}
    </Box>
  );
};

export default FormSection;
